<template>
  <div class="card-maquina" :style="cardBorderStyle">
    <div class="status-bar">
      <div class="status-bar-track" :style="{ background: colorEstatus(item.status) + '22' }"></div>
      <div class="status-bar-fill" :style="{ background: colorEstatus(item.status) }"></div>
    </div>

    <div class="card-header">
      <div class="machine-id-block">
        <div class="machine-label-row">
          <v-icon size="13" :color="colorEstatus(item.status)" class="mr-1">{{ statusIcon }}</v-icon>
          <span class="machine-label">Maquina</span>
        </div>
        <span class="machine-code" :style="machineCodeStyle">{{ item.codigoMaquina }}</span>
      </div>
      <div class="header-right">
        <div class="status-pill" :style="pillStyle">
          <span class="status-led" :style="{ background: colorEstatus(item.status) }"></span>
          {{ labelEstatus(item.status) }}
        </div>
      </div>
    </div>

    <template v-if="!isInactive">
      <div class="oee-section">
        <div class="ring-wrap">
          <svg viewBox="0 0 90 90" width="90" height="90" style="transform:rotate(-90deg)">
            <circle cx="45" cy="45" r="36" fill="none" stroke="#F0F2F6" stroke-width="7"/>
            <circle
              cx="45"
              cy="45"
              r="36"
              fill="none"
              :stroke="oeeColor"
              stroke-width="7"
              stroke-linecap="round"
              :stroke-dasharray="`${oeeArc} ${circumference}`"
            />
          </svg>
          <div class="ring-center">
            <template v-if="item.oee != null">
              <ICountUp class="ring-value" :endVal="item.oee" :options="{ decimalPlaces: 0 }" />
              <span class="ring-pct">%</span>
            </template>
            <template v-else>
              <span class="ring-value">—</span>
            </template>
            <div class="ring-sub">OEE</div>
          </div>
        </div>

        <div class="kpi-trio">
          <div class="kpi-row" v-for="k in kpis" :key="k.name">
            <div class="kpi-label-block">
              <div class="kpi-dot" :style="{ background: k.color }"></div>
              <span class="kpi-name">{{ k.name }}</span>
            </div>
            <div class="kpi-bar-wrap">
              <div class="kpi-bar-fill" :style="{ width: k.val + '%', background: k.color }"></div>
            </div>
            <span class="kpi-value">{{ k.display }}</span>
          </div>
        </div>
      </div>

      <div v-if="showTcRow" class="tc-row">
        <div class="tc-chip">
          <span class="tc-chip-label">TC Esperado</span>
          <span class="tc-chip-value qad">{{ item.tcQadTxt }}</span>
        </div>
        <div class="tc-chip">
          <span class="tc-chip-label">TC Real</span>
          <span class="tc-chip-value">{{ item.tcRealTxt }}</span>
        </div>
      </div>

      <div class="h-divider"></div>

      <div v-if="showParoBlock" class="paro-section" :class="{ 'paro-np': item.status === 4 }">
        <p class="paro-title">Paro en curso</p>
        <div class="paro-timer">
          <span class="paro-timer-hms">{{ timerHMS }}</span><span class="paro-timer-ms">.{{ timerMS }}</span>
        </div>
        <p class="paro-meta">Piezas Ok: {{ safeNumber(item.pizasOk) }}</p>
      </div>

      <div class="prod-section">
        <div class="prod-box">
          <span class="prod-box-label">Piezas Ok</span>
          <span class="prod-box-value cool-primary">{{ safeNumber(item.pizasOk) }}</span>
        </div>
        <div class="prod-box">
          <span class="prod-box-label">Objetivo Turno</span>
          <span class="prod-box-value cool-secondary">{{ safeNumber(item.planeacionPorTurno) }}</span>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="inactive-state">
        <div class="inactive-icon">
          <v-icon size="22" color="#8A95A3">mdi-minus-circle-outline</v-icon>
        </div>
        <p class="inactive-text">Inactiva</p>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import ICountUp from "vue-countup-v2";

const R = 36;
const CIRCUMFERENCE = 2 * Math.PI * R;

const STATUS_MAP = {
  0: { label: "Inactiva", color: "#8A95A3", icon: "mdi-minus-circle-outline" },
  1: { label: "Activa", color: "#00C472", icon: "mdi-cog" },
  3: { label: "Paro Programado", color: "#FF9F00", icon: "mdi-cog" },
  4: { label: "Paro No Prog.", color: "#E83535", icon: "mdi-cog" },
};

export default {
  name: "CardMaquinaNyx",
  components: { ICountUp },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    segundos: 0,
    milisegundos: 0,
    interval: null,
    intervalMs: null,
  }),
  computed: {
    isInactive() {
      return this.item?.status === 0;
    },
    mostrarSegundero() {
      return this.item?.status === 3 || this.item?.status === 4;
    },
    showParoBlock() {
      return this.item?.status === 3 || this.item?.status === 4;
    },
    showTcRow() {
      return (this.item.tcRealTxt && this.item.tcRealTxt !== "—") || this.item.tcQadSegPza > 0;
    },
    timerHMS() {
      const s = this.mostrarSegundero ? this.segundos : (this.item.tiempoParoSegundos || 0);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      const pad = n => String(n).padStart(2, "0");
      return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
    },
    timerMS() {
      return String(this.milisegundos).padStart(2, "0");
    },
    circumference() {
      return CIRCUMFERENCE;
    },
    oeeColor() {
      const oee = Number(this.item?.oee);
      if (!Number.isFinite(oee)) return "#8A95A3";
      if (oee >= 85) return "#00C472";
      if (oee >= 78) return "#FF9F00";
      return "#E83535";
    },
    oeeArc() {
      const val = Math.min(100, Math.max(0, Number(this.item?.oee ?? 0)));
      return ((val / 100) * CIRCUMFERENCE).toFixed(2);
    },
    kpis() {
      const fmt = v => {
        const n = Number(v);
        return Number.isFinite(n) ? `${n.toFixed(1)}%` : "—";
      };
      return [
        { name: "Disponib.", val: Math.min(100, Number(this.item?.disponibilidad ?? 0)), color: "#2B7EFF", display: fmt(this.item?.disponibilidad) },
        { name: "Eficiencia", val: Math.min(100, Number(this.item?.rendimiento ?? 0)), color: "#00C472", display: fmt(this.item?.rendimiento) },
        { name: "Calidad", val: Math.min(100, Number(this.item?.calidad ?? 0)), color: "#FF9F00", display: fmt(this.item?.calidad) },
      ];
    },
    cardBorderStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        border: `1.5px solid ${c}30`,
        boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06), 0 0 0 1px ${c}15`,
      };
    },
    pillStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        color: c,
        background: c + "14",
        borderColor: c + "40",
      };
    },
    machineCodeStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        color: c,
        textShadow: `${c}22 0 0 12px`,
      };
    },
    statusIcon() {
      return (STATUS_MAP[this.item?.status] || STATUS_MAP[0]).icon;
    },
  },
  created() {
    try {
      if (this.item._segundosInicialesParo > 0) {
        this.segundos = this.item._segundosInicialesParo;
      } else if (this.item.fechaInicio) {
        const diff = moment().valueOf() - moment(this.item.fechaInicio).valueOf();
        this.segundos = Math.max(0, Math.trunc(diff / 1000));
        if (isNaN(this.segundos)) this.segundos = 0;
      } else {
        this.segundos = Number(this.item?.tiempoParoSegundos || 0);
      }
      this.iniciar();
      this.iniciarMs();
    } catch {
      this.segundos = 0;
      this.iniciar();
      this.iniciarMs();
    }
  },
  destroyed() {
    this.detener();
    this.detenerMs();
  },
  methods: {
    colorEstatus(status) {
      return (STATUS_MAP[status] || STATUS_MAP[0]).color;
    },
    labelEstatus(status) {
      return (STATUS_MAP[status] || STATUS_MAP[0]).label;
    },
    safeNumber(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n.toLocaleString() : "0";
    },
    iniciar() {
      if (this.interval) return;
      this.interval = setInterval(() => {
        if (this.showParoBlock) this.segundos += 1;
      }, 1000);
    },
    detener() {
      if (!this.interval) return;
      clearInterval(this.interval);
      this.interval = null;
    },
    iniciarMs() {
      if (this.intervalMs) return;
      this.intervalMs = setInterval(() => {
        if (!this.mostrarSegundero) return;
        this.milisegundos = (this.milisegundos + 1) % 100;
      }, 10);
    },
    detenerMs() {
      if (!this.intervalMs) return;
      clearInterval(this.intervalMs);
      this.intervalMs = null;
    },
  },
};
</script>

<style scoped>
.card-maquina {
  --radius-card: 20px;
  --radius-inner: 12px;
  --radius-sm: 8px;
  --clr-bg: #F4F6F9;
  --clr-surface: #F0F2F6;
  --clr-border: rgba(0,0,0,0.07);
  --clr-text-primary: #0D1117;
  --clr-text-secondary: #5A6476;
  --clr-text-muted: #8A95A3;
  --font-primary: 'Space Grotesk', 'Roboto', sans-serif;
  --font-body: 'DM Sans', 'Roboto', sans-serif;
}

.card-maquina {
  background: #FFFFFF;
  border-radius: var(--radius-card);
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  font-family: var(--font-body);
  display: flex;
  flex-direction: column;
}

.card-maquina:hover {
  transform: translateY(-3px);
}

.status-bar {
  height: 4px;
  width: 100%;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.status-bar-track {
  position: absolute;
  inset: 0;
}

.status-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 40%;
  border-radius: 0 2px 2px 0;
  animation: bar-sweep 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes bar-sweep {
  0%   { left: -45%; width: 40%; opacity: 1; }
  60%  { left: 100%; width: 55%; opacity: 0.8; }
  100% { left: 110%; width: 40%; opacity: 0; }
}

.card-header {
  padding: 16px 20px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--clr-border);
  flex-shrink: 0;
}

.machine-id-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.machine-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  font-family: var(--font-primary);
}

.machine-code {
  font-family: var(--font-primary);
  font-size: 22px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.status-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 30px;
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border: 1.5px solid;
  transition: background 0.3s, color 0.3s;
}

.status-led {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.oee-section {
  padding: 18px 20px 12px;
  display: flex;
  align-items: center;
  gap: 18px;
}

.ring-wrap {
  position: relative;
  width: 90px;
  height: 90px;
  flex-shrink: 0;
}

.ring-wrap svg circle:last-child {
  transition: stroke-dasharray 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.ring-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  line-height: 1;
  pointer-events: none;
}

.ring-value {
  font-family: var(--font-primary);
  font-size: 19px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.02em;
}

.ring-pct {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
}

.ring-sub {
  display: block;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  margin-top: 2px;
  font-family: var(--font-primary);
}

.kpi-trio {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.kpi-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kpi-label-block {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 76px;
}

.kpi-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.kpi-name {
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-text-secondary);
}

.kpi-bar-wrap {
  flex: 1;
  height: 5px;
  background: var(--clr-surface);
  border-radius: 10px;
  overflow: hidden;
}

.kpi-bar-fill {
  height: 100%;
  border-radius: 10px;
  opacity: 0.75;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.kpi-value {
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  color: var(--clr-text-primary);
  min-width: 40px;
  text-align: right;
}

.tc-row {
  padding: 0 20px 14px;
  display: flex;
  gap: 8px;
}

.tc-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 8px;
  background: var(--clr-surface);
  border: 1px solid var(--clr-border);
  flex: 1;
  justify-content: center;
}

.tc-chip-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
  font-family: var(--font-primary);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.tc-chip-value {
  font-size: 12px;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--clr-text-primary);
}

.tc-chip-value.qad {
  color: #2B7EFF;
}

.h-divider {
  height: 1px;
  background: var(--clr-border);
  margin: 0 20px;
  flex-shrink: 0;
}

.paro-section {
  margin: 10px 20px 4px;
  background: var(--clr-surface);
  border-radius: var(--radius-inner);
  padding: 13px 15px;
  border-left: 3px solid #FF9F00;
  flex-shrink: 0;
}

.paro-section.paro-np {
  border-left-color: #E83535;
}

.paro-title {
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  margin-bottom: 6px;
}

.paro-timer {
  display: flex;
  align-items: baseline;
  gap: 1px;
  font-family: var(--font-primary);
  line-height: 1;
  margin-bottom: 5px;
}

.paro-timer-hms {
  font-size: 26px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}

.paro-timer-ms {
  font-size: 16px;
  font-weight: 500;
  color: var(--clr-text-muted);
  font-variant-numeric: tabular-nums;
}

.paro-meta {
  font-size: 11px;
  color: var(--clr-text-secondary);
  line-height: 1.4;
}

.prod-section {
  padding: 12px 20px 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(90px, 1fr));
  gap: 8px;
  flex-shrink: 0;
}

.prod-box {
  background: var(--clr-surface);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.prod-box-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: var(--font-primary);
}

.prod-box-value {
  font-family: var(--font-primary);
  font-size: 20px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.prod-box-value.cool-primary { color: #0f5f97; }
.prod-box-value.cool-secondary { color: #5b6cff; }

.inactive-state {
  padding: 32px 20px;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.inactive-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--clr-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
}

.inactive-text {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 600;
  color: var(--clr-text-secondary);
}

@keyframes card-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.card-maquina {
  animation: card-in 0.4s ease both;
}

@media (max-width: 600px) {
  .machine-code { font-size: 18px; }
  .ring-value { font-size: 16px; }
  .paro-timer-hms { font-size: 22px; }
  .prod-box-value { font-size: 18px; }
  .oee-section { padding: 14px 16px 10px; }
  .card-header { padding: 14px 16px 12px; }
  .tc-row { padding: 0 16px 12px; }
}

@media (min-width: 1265px) {
  .machine-code { font-size: 20px; }
  .ring-value { font-size: 18px; }
  .kpi-name { font-size: 10px; }
  .kpi-value { font-size: 10px; }
  .prod-box-value { font-size: 18px; }
}

.machine-label-row {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 2px;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
</style>
